import { useState } from 'react';
import styled from '@emotion/styled';
import { mediaQuery, Spacings, Fonts } from '@styles/index';
import Image from '@base/Image';
import Drawer from '@base/Drawer/Drawer';
import H4 from '@base/H4/H4';
import H5 from '@base/H5/H5';
import H6 from '@base/H6/H6';
import P1 from '@base/P1/P1';
import P2 from '@base/P2/P2';
import { getDurationText, commas, currencySymbolMap } from '@utils/textHelper';
import { segment } from '@actions/analytics';
import useGoalEventProps from '@hooks/useGoalEventProps';
import { useSelectedGoalUID } from '@api/hooks/topology/useSelectedGoal';
import RightChevron from 'aquilla/icons/actions/RightChevron';
import Typography from 'aquilla/core/atoms/Typography';
import { REFERRAL_REWARD_INFO_CLICKED } from '../constants';

const RightArrow = styled(RightChevron)`
  opacity: 0;
`;

const ListItemWrapper = styled.div`
  display: flex;
  padding: ${Spacings.SPACING_4B};
  justify-content: space-between;
  border-radius: ${Spacings.SPACING_2B};
  cursor: pointer;
  align-items: center;
  &:hover {
    background: var(--color-base-light-0-secondary);
    ${RightArrow} {
      opacity: 1;
    }
  }
  ${mediaQuery.uptoMobile} {
    margin-bottom: ${Spacings.SPACING_0B};
  }
`;

const ViewAllWrapper = styled(ListItemWrapper)`
  justify-content: center;
  align-items: center;
  border-top: ${Spacings.SPACING_1} solid var(--color-divider);
  margin-bottom: ${Spacings.SPACING_0B};
  padding: ${Spacings.SPACING_4B};
`;

const LeftWrapper = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_4B};
`;

const AvatarHolder = styled.div`
  width: ${Spacings.SPACING_10B};
  height: ${Spacings.SPACING_10B};
  position: relative;
`;

const InfoHolder = styled.div``;

const RewardItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_1B};
  margin-top: ${Spacings.SPACING_1B};
`;

const RewardItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_1B};
`;

const RewardIconHolder = styled.div`
  width: 11px;
  height: 11px;
  position: relative;
`;

const RewardText = styled(P2)`
  color: var(--color-i-gray-13);
  font-weight: ${Fonts.SEMIBOLD};
`;

const ReferralDetailWrapper = styled.div`
  grid-column: span 10;
  margin-top: ${Spacings.SPACING_1B};
`;

const ReferralDetailInfoWrapper = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_4B};
  align-items: center;
`;

const ReferralDetailInfo = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_1B};
  flex-direction: column;
`;

const HR = styled.hr`
  background-color: var(--color-divider);
  height: 1px;
  width: 100%;
  border: none;
  margin: ${Spacings.SPACING_8B} ${Spacings.SPACING_0B};
`;

const ReferralDetailsRewards = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_6B};
`;

const RewardDetailItem = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_4B};
  align-items: center;
`;

const NoRewardsWrapper = styled.div``;

const SubHeading = styled(H5)``;

const NoRewardsPointsWrapper = styled.div`
  margin-top: ${Spacings.SPACING_8B};
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
`;

const NoRewardsPoints = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_2B};
  flex-shrink: 0;
`;

const NoRewardsIcon = styled.div`
  position: relative;
  width: ${Spacings.SPACING_5B};
  height: ${Spacings.SPACING_5B};
  flex-shrink: 0;
`;

const ReferralDetail = ({
  drawerState,
  closeReferralDetailDrawer,
  selectedReferral,
  noRewards
}) => {
  const { user, subscriptionDetails, referralRewards } = selectedReferral ?? {};

  return (
    <Drawer drawerState={drawerState} onClose={closeReferralDetailDrawer}>
      <ReferralDetailWrapper>
        {user && (
          <ReferralDetailInfoWrapper>
            <AvatarHolder>
              <Image
                src={user.avatar || 'referrals/placeholder_avatar.svg'}
                layout="fill"
                alt="avatar"
              />
            </AvatarHolder>

            <ReferralDetailInfo>
              <H4>
                {user.firstName} {user.lastName}
              </H4>
              {subscriptionDetails ? (
                <P1>
                  {`${subscriptionDetails.goalName} • ${getDurationText(
                    subscriptionDetails.duration
                  )} •
                  ${currencySymbolMap()}${commas(
                    subscriptionDetails.paidAmount
                  )}`}
                </P1>
              ) : null}
            </ReferralDetailInfo>
          </ReferralDetailInfoWrapper>
        )}

        <HR />
        {referralRewards?.length ? (
          <ReferralDetailsRewards>
            <SubHeading>Rewards earned for this referral</SubHeading>
            {referralRewards.map((reward, index) => (
              <RewardDetailItem key={String(index)}>
                <AvatarHolder>
                  <Image
                    src={reward.iconUrl || 'referrals/placeholder_avatar.svg'}
                    layout="fill"
                    alt="avatar"
                  />
                </AvatarHolder>
                <ReferralDetailInfo>
                  <H6>{reward.title}</H6>
                  <P1>{reward.description}</P1>
                </ReferralDetailInfo>
              </RewardDetailItem>
            ))}
          </ReferralDetailsRewards>
        ) : (
          <NoRewardsWrapper>
            <SubHeading>{noRewards?.extraBlockInfo?.label}</SubHeading>
            <NoRewardsPointsWrapper>
              {noRewards?.reasons.map((reason, index) => (
                <NoRewardsPoints key={String(index)}>
                  <NoRewardsIcon>
                    <Image
                      src="referrals/checkmark.svg"
                      layout="fill"
                      alt="avatar"
                    />
                  </NoRewardsIcon>
                  <P1>{reason}</P1>
                </NoRewardsPoints>
              ))}
            </NoRewardsPointsWrapper>
          </NoRewardsWrapper>
        )}
      </ReferralDetailWrapper>
    </Drawer>
  );
};

const ReferralList = ({ referralData, noRewards }) => {
  const REFER_COUNT = 5;
  const selectedGoalUID = useSelectedGoalUID();
  const goalSegmentData = useGoalEventProps({ goalUID: selectedGoalUID });

  const [referralsList, setReferralsList] = useState(
    referralData.slice(0, REFER_COUNT)
  );
  const [drawerState, setDrawerState] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);
  const [viewAll, setViewAll] = useState(referralData.length >= REFER_COUNT);

  const closeReferralDetailDrawer = () => {
    setDrawerState(false);
    setSelectedReferral(null);
  };

  const handleViewAll = () => {
    setViewAll(true);
    setReferralsList(referralData);
  };

  const handleReferralClick = (index) => {
    setSelectedReferral(referralData[index]);
    setDrawerState(true);
    segment.track(REFERRAL_REWARD_INFO_CLICKED, goalSegmentData);
  };

  return (
    <>
      {referralsList.map((referral, index) => (
        <ListItemWrapper
          key={String(index)}
          $border={index !== referralData.length - 1}
          onClick={() => handleReferralClick(index)}
        >
          <LeftWrapper>
            <AvatarHolder>
              <Image
                src={
                  referral.user?.avatar ?? 'referrals/placeholder_avatar.svg'
                }
                layout="fill"
                alt="avatar"
              />
            </AvatarHolder>
            <InfoHolder>
              <H5>
                {referral.user?.firstName} {referral.user?.lastName}
              </H5>
              <RewardItemWrapper>
                {referral.referralRewards?.map((reward, i) => (
                  <div key={String(i)}>
                    <RewardItem>
                      <RewardIconHolder>
                        <Image
                          src="referrals/yellow_stars.svg"
                          alt="stars"
                          layout="fill"
                        />
                      </RewardIconHolder>
                      <RewardText>{`${reward.title}`}</RewardText>
                    </RewardItem>
                  </div>
                ))}
                {!referral.referralRewards?.length && (
                  <RewardItem>
                    <P2 color="var(--color-text-secondary)">
                      Not eligible for a reward
                    </P2>
                  </RewardItem>
                )}
              </RewardItemWrapper>
            </InfoHolder>
          </LeftWrapper>
          <RightArrow
            width={Spacings.SPACING_6B}
            height={Spacings.SPACING_6B}
          />
        </ListItemWrapper>
      ))}
      {viewAll && referralsList.length !== referralData.length && (
        <ViewAllWrapper onClick={handleViewAll}>
          <Typography variant="h6">View all</Typography>
          <RightChevron width={20} height={20} />
        </ViewAllWrapper>
      )}
      <ReferralDetail
        drawerState={drawerState}
        closeReferralDetailDrawer={closeReferralDetailDrawer}
        selectedReferral={selectedReferral}
        noRewards={noRewards}
      />
    </>
  );
};

export default ReferralList;
